"use client"

import type React from "react"
import type { SetupData } from "../SetupFlow"
import "./ReviewSyncSetup.css"

interface ReviewSyncSetupProps {
  setupData: SetupData
  onSubmit: () => void
  onBack: () => void
}

const ReviewSyncSetup: React.FC<ReviewSyncSetupProps> = ({ setupData, onSubmit, onBack }) => {
  const handleViewDetails = () => {
    // Navigate back to field mapping for details
    onBack()
  }

  return (
    <div className="review-sync-setup">
      <div className="review-header">
        <h1>Review Your Sync Setup</h1>
        <p>Here's a summary of your sync configuration.</p>
        <p>Please review all details before you start syncing.</p>
      </div>

      <div className="review-sections">
        {/* Connector Section */}
        <div className="review-section connector-section">
          <h2>Connector</h2>
          <div className="connector-display">
            <div className="platform-logo hubspot">
              <img
                src={setupData.selectedConnector?.fromLogo || "https://cdn.worldvectorlogo.com/logos/hubspot-1.svg"}
                alt={setupData.selectedConnector?.from || "HubSpot"}
              />
            </div>
            <div className="connector-arrow">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14" />
                <path d="M12 5l7 7-7 7" />
              </svg>
            </div>
            <div className="platform-logo salesforce">
              <img
                src={setupData.selectedConnector?.toLogo || "https://cdn.worldvectorlogo.com/logos/salesforce-2.svg"}
                alt={setupData.selectedConnector?.to || "Salesforce"}
              />
            </div>
          </div>
        </div>

        {/* Sync Direction Section */}
        <div className="review-section direction-section">
          <h2>Sync Direction</h2>
          <div className="direction-value">{setupData.syncDirection || "Bidirectional"}</div>
        </div>

        {/* Field Mapping Section */}
        <div className="review-section mapping-section">
          <div className="mapping-header">
            <h2>Field Mapping Overview</h2>
            <button className="view-details-btn" onClick={handleViewDetails}>
              View Details
            </button>
          </div>

          <div className="mapping-table">
            <div className="mapping-headers">
              <div className="header-left">HubSpot Fields</div>
              <div className="header-right">Salesforce Fields</div>
            </div>

            <div className="mapping-rows">
              {setupData.fieldMappings.map((mapping) => (
                <div key={mapping.id} className="mapping-row">
                  <div className="field-left">{mapping.hubspotField}</div>
                  <div className="mapping-arrow">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14" />
                      <path d="M12 5l7 7-7 7" />
                    </svg>
                  </div>
                  <div className="field-right">{mapping.salesforceField}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Warning Section */}
      <div className="warning-section">
        <div className="warning-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>
        <div className="warning-content">
          <p>
            <strong>Make sure required fields are mapped.</strong>
          </p>
          <p>You'll be able to monitor sync progress and logs from the Dashboard.</p>
          <p>✓ You can change sync settings anytime.</p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="actions-section">
        <button className="back-button" onClick={onBack}>
          Back
        </button>
        <button className="start-syncing-button" onClick={onSubmit}>
          Start Syncing
        </button>
      </div>
    </div>
  )
}

export default ReviewSyncSetup
